// frontend/src/homepage/useLiveMeshPageChrome.js
//
// The three page-level effects every live-mesh page needs, in one hook.
//
// Homepage.js and ProfessionLanding.js are the same page with different words,
// and until now each carried its own private copy of these three (backlog
// #278). They are behaviourally identical - the profession page's copies were
// written by copying the homepage's - so a divergence between them could only
// ever be a bug, and the comments below are the homepage's originals.
//
// CALL ORDER IS PART OF THE CONTRACT: background, then the debug hooks, then
// the hash scroll, which is the order both pages called them in. Effects run in
// call order, and the hash scroll reads an element the page has already
// committed, so it must stay last.
//
// Nothing here renders anything: no page's DOM changes by adopting it, which is
// what lets the homepage's six invariant suites stand as the byte-identity
// oracle for the move.

import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { applyHowDebug, applySmokeLook, parseHowDebug, parseSmokeLook } from "./howDebug";
import { getMotionState } from "../motion/motionSignal";

/**
 * Paint the page's own ground colour on <body>, for as long as the page is
 * mounted.
 *
 * The fixed wash layers cover the viewport, but the body still shows through an
 * overscroll bounce - and the app's default white against this page's #F9FDFC
 * reads as a flash at the top and bottom of every rubber-band scroll. A class
 * added and removed with the page keeps that scoped: no global stylesheet is
 * edited and no other route is touched.
 */
function usePageBackground() {
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    document.body.classList.add("hl-page-bg");
    return () => document.body.classList.remove("hl-page-bg");
  }, []);
}

/**
 * DEVELOPMENT ONLY: `?hl_debug=how:<p>` steers the pinned how-it-works section
 * to a scroll progress and hides the sections above it, so a headless capture
 * (which cannot scroll) can photograph each stage; `?hl_smoke=<look>` picks a
 * smoke-plume look (smokePlume.js LOOKS) for the same captures. Inert in
 * production - every helper in howDebug.js checks NODE_ENV first, and the
 * query string is never read there.
 */
function useHowDebug() {
  useEffect(() => {
    if (process.env.NODE_ENV === "production" || typeof window === "undefined") return undefined;
    const undoHow = applyHowDebug(parseHowDebug(window.location.search));
    const undoLook = applySmokeLook(parseSmokeLook(window.location.search));
    return () => {
      undoHow();
      undoLook();
    };
  }, []);
}

/**
 * Honour a fragment in the URL (e.g. "/#supporter-network", the id the
 * features tile 04 still carries). The plan bullets no longer point at it -
 * they link to the dedicated /supporter-network page - but the anchor is a
 * public, shareable address that outside links (marketing posts, emails) may
 * still use, so the scroll behaviour stays.
 *
 * The browser resolves a fragment only on a FULL page load. A React Router
 * navigation - including a same-path one that changes only the hash - just
 * pushes history, leaving the scroll position untouched. Without this effect
 * such a link would look dead. rAF defers to after the commit so the target
 * element exists; every section on these pages is eagerly imported, so one
 * frame is enough.
 */
function useScrollToHash() {
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return undefined;
    const id = hash.slice(1);
    const frame = requestAnimationFrame(() => {
      // A visitor who asked for no motion gets the jump, not the glide.
      const behavior = getMotionState().off ? "auto" : "smooth";
      document.getElementById(id)?.scrollIntoView({ behavior, block: "start" });
    });
    return () => cancelAnimationFrame(frame);
  }, [hash]);
}

/** Every live-mesh page's chrome effects, in the order they must run. */
export default function useLiveMeshPageChrome() {
  usePageBackground();
  useHowDebug();
  useScrollToHash();
}
